import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { TranslatorService } from './translator_service';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private apiKey = environment.apiKey;
  private loadPromise: Promise<void> | null = null;

  /** Language the currently loaded script was requested with (null = nothing loaded yet). */
  private loadedLanguage: string | null = null;
  /**
   * Language to use for the next load. Set from the language emitter, which fires
   * BEFORE localStorage is updated in some flows (login / MFA), so the emitted
   * value - not localStorage - is the source of truth for a reload.
   */
  private languageOverride: string | null = null;
  /** Guards against patching console.warn more than once (see silenceDuplicateCustomElementWarnings). */
  private duplicateElementWarningsSilenced = false;

  /**
   * Reactive readiness flag. Components can gate `<google-map>` directly with
   * `@if (googleMapsLoader.ready())` instead of each maintaining a local flag.
   */
  readonly ready = signal(false);

  /**
   * Emits after the API has been re-loaded in a new language. Maps built
   * declaratively in a template come back on their own; components that add
   * markers/overlays imperatively use this to re-apply them to the fresh map.
   */
  readonly reloaded$ = new Subject<void>();

  constructor(private translator: TranslatorService) {
    /* The Google Maps JS API fixes its language at script load time - there is no
       runtime setter. The old project got away with it because every language
       change did a full page reload, which re-requested the script. Now that the
       app behaves as a real SPA, re-request it here instead: one central hook
       covers every place the language can change (header menu, login, MFA),
       so no component needs to know about it. */
    this.translator.currentLangEmitter$.subscribe((lang) => {
      void this.reloadWithLanguage(String(lang || ''));
    });
  }

  loadGoogleMaps(): Promise<void> {
    // Cache a single in-flight promise so the script is requested exactly once,
    // even if several map components initialize concurrently.
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      let language = this.languageOverride || localStorage.getItem('language') || 'he';
      this.loadedLanguage = language;
      this.languageOverride = null;

      // Script may already be present (e.g. after a previous load).
      if ((window as any).google?.maps) {
        this.ready.set(true);
        resolve();
        return;
      }

      const callbackName = '__onGoogleMapsApiReady';
      // With loading=async the script's onload fires before google.maps is
      // initialized. The callback param is invoked only once the API core is
      // fully ready, so google.maps.* (e.g. ControlPosition) is guaranteed defined.
      (window as any)[callbackName] = () => {
        this.ready.set(true);
        resolve();
        try { delete (window as any)[callbackName]; } catch { /* noop */ }
      };
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,geometry&language=${language}&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.onerror = (error) => {
        // Allow a retry on a later call if the script failed to load.
        this.loadPromise = null;
        this.loadedLanguage = null;
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Re-requests the API in `language` when it differs from what is loaded.
   * `ready` drops to false for the duration, so components gating on it unmount
   * their map and mount a fresh one against the new-language API. Map components
   * created later (all the dialogs) simply pick the new language up on open.
   */
  async reloadWithLanguage(language: string): Promise<void> {
    const lang = String(language || '');
    if (!lang) {
      return;
    }

    // Never tear the script down mid-flight: let a first load settle first.
    if (this.loadPromise) {
      await this.loadPromise.catch(() => { /* reported to whoever started that load */ });
    }

    // Nothing loaded (never started, or the load failed): just remember the
    // language so the next load requests it.
    if (!this.loadPromise || this.loadedLanguage === lang) {
      this.languageOverride = lang;
      return;
    }

    this.languageOverride = lang;
    this.silenceDuplicateCustomElementWarnings();
    this.ready.set(false);
    this.loadPromise = null;
    this.removeApiScripts();
    this.clearApiGlobals();

    try {
      await this.loadGoogleMaps();
      this.reloaded$.next();
    } catch {
      /* Offline / blocked: stay not-ready (gated maps hide, exactly as on a failed
         first load) and let the next language change try again. */
    }
  }

  /**
   * Silences the ~124 `Element with name "gmp-..." already defined.` warnings the
   * Maps bundle prints on a second load.
   *
   * Custom elements can never be unregistered, so the first load's `gmp-*`
   * definitions outlive the script that made them. The re-loaded bundle checks the
   * registry itself and logs one warning per element instead of throwing (verified:
   * intercepting customElements.define catches nothing, the warnings still appear),
   * so the message is the only thing there is to act on. Only that exact sentence
   * is dropped - every other console.warn, including Google's own, still comes
   * through. Left installed for the rest of the page's life because the API loads
   * some of its modules lazily (marker/places/...), which would otherwise warn
   * later, long after a language change.
   */
  private silenceDuplicateCustomElementWarnings(): void {
    if (this.duplicateElementWarningsSilenced) {
      return;
    }
    this.duplicateElementWarningsSilenced = true;

    const originalWarn = console.warn.bind(console);
    const duplicateElementWarning = /^Element with name "[^"]+" already defined\.?$/;
    console.warn = (...args: any[]) => {
      const message = args[0];
      if (typeof message === 'string' && duplicateElementWarning.test(message.trim())) {
        return;
      }
      originalWarn(...args);
    };
  }

  /** Drops every Maps script tag so a fresh one is fetched with the new language. */
  private removeApiScripts(): void {
    const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    scripts.forEach((script) => script.parentNode?.removeChild(script));
  }

  /**
   * The API bails out early ("included multiple times") if it finds itself
   * already initialized, so its namespace has to go before re-injecting.
   */
  private clearApiGlobals(): void {
    const globalScope = window as any;
    if (!globalScope.google) {
      return;
    }
    try {
      delete globalScope.google.maps;
      if (Object.keys(globalScope.google).length === 0) {
        delete globalScope.google;
      }
    } catch { /* noop */ }
  }
}
